<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-account-tie-outline me-2"></i>{{ $t('page.hr_self', 'HR Self-Service') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='leave' }" @click="tab='leave'"><i class="mdi mdi-airplane me-1"></i>Leave</button>
        <button class="hms-tab" :class="{ active: tab==='licenses' }" @click="tab='licenses'; loadLicenses()"><i class="mdi mdi-card-account-details me-1"></i>Licenses</button>
        <button class="hms-tab" :class="{ active: tab==='training' }" @click="tab='training'; loadTrainings()"><i class="mdi mdi-school me-1"></i>Training / CME</button>
        <button class="hms-tab" :class="{ active: tab==='appraisals' }" @click="tab='appraisals'; loadAppraisals()"><i class="mdi mdi-clipboard-check me-1"></i>Appraisals</button>
      </div>

      <!-- ============ LEAVE ============ -->
      <div v-if="tab==='leave'">
        <div class="hms-stat-row">
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ leave.stats.pending || 0 }}</div><div class="hms-stat-lbl">Pending approval</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ leave.stats.on_leave_today || 0 }}</div><div class="hms-stat-lbl">On leave today</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ leave.stats.upcoming_7d || 0 }}</div><div class="hms-stat-lbl">Upcoming (7d)</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ leave.stats.approved_days_ytd || 0 }}</div><div class="hms-stat-lbl">Approved days YTD</div></div>
          <div class="hms-stat"><button class="hms-btn hms-btn-success" @click="openLeaveModal()"><i class="mdi mdi-plus"></i> Apply leave</button></div>
        </div>
        <div class="d-flex gap-2 mb-2">
          <select v-model="leave.statusFilter" class="hms-input" @change="loadLeaves" style="max-width:200px">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <table class="hms-table">
          <thead><tr><th>#</th><th>Staff</th><th>Type</th><th>Period</th><th>Days</th><th>Reason</th><th>Status</th><th>Decided by</th><th></th></tr></thead>
          <tbody>
            <tr v-for="l in leave.list" :key="l.leave_id" :class="leaveRow(l)">
              <td>#{{ l.leave_id }}</td>
              <td>{{ l.staff_name }}<div class="hms-muted-sm">{{ l.staff_number }} · {{ l.staff_dept || '' }}</div></td>
              <td><span class="hms-pill" :class="leaveTypePill(l.leave_type)">{{ l.leave_type }}</span></td>
              <td>{{ l.start_date }} → {{ l.end_date }}</td>
              <td><strong>{{ l.days_requested }}</strong></td>
              <td class="hms-muted-sm">{{ l.reason }}</td>
              <td><span class="hms-pill" :class="leaveStatusPill(l.status)">{{ l.status }}</span></td>
              <td>{{ l.approver_name || '—' }}<div v-if="l.rejection_reason" class="hms-muted-sm">{{ l.rejection_reason }}</div></td>
              <td>
                <template v-if="l.status === 'pending'">
                  <button class="hms-btn hms-btn-success hms-btn-sm" @click="decideLeave(l, 'approved')">Approve</button>
                  <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="decideLeave(l, 'rejected')">Reject</button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ LICENSES ============ -->
      <div v-if="tab==='licenses'">
        <div class="hms-stat-row">
          <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ licenses.stats.total || 0 }}</div><div class="hms-stat-lbl">Total licenses</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ licenses.stats.valid_count || 0 }}</div><div class="hms-stat-lbl">Valid</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ licenses.stats.due_soon || 0 }}</div><div class="hms-stat-lbl">Due in 60d</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ licenses.stats.expired_count || 0 }}</div><div class="hms-stat-lbl">Expired</div></div>
          <div class="hms-stat"><button class="hms-btn hms-btn-success" @click="openLicenseModal()"><i class="mdi mdi-plus"></i> Add license</button></div>
        </div>
        <table class="hms-table">
          <thead><tr><th>Staff</th><th>Regulator</th><th>License #</th><th>Category</th><th>Issued</th><th>Expires</th><th>Days left</th><th>Status</th><th></th></tr></thead>
          <tbody>
            <tr v-for="l in licenses.list" :key="l.license_id" :class="licenseRow(l)">
              <td>{{ l.staff_name }}<div class="hms-muted-sm">{{ l.staff_number }}</div></td>
              <td><strong>{{ l.regulator }}</strong><span v-if="l.regulator_other"> ({{ l.regulator_other }})</span></td>
              <td>{{ l.license_number }}</td>
              <td>{{ l.license_category || '—' }}</td>
              <td>{{ l.issue_date || '—' }}</td>
              <td>{{ l.expiry_date || '—' }}</td>
              <td :class="daysToExpiryClass(l.days_to_expiry)"><strong>{{ l.days_to_expiry != null ? l.days_to_expiry + 'd' : '—' }}</strong></td>
              <td><span class="hms-pill" :class="licenseStatusPill(l.renewal_status)">{{ l.renewal_status }}</span></td>
              <td><button class="hms-btn-link" @click="openLicenseModal(l)"><i class="mdi mdi-pencil"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ TRAINING / CME ============ -->
      <div v-if="tab==='training'">
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-success" @click="openTrainingModal()"><i class="mdi mdi-plus"></i> Record Training</button>
        </div>
        <div class="hms-grid-2">
          <div class="hms-card">
            <div class="hms-card-head">CME points by staff (this year)</div>
            <div class="hms-card-body p-0">
              <table v-if="training.cmeByStaff.length" class="hms-table">
                <thead><tr><th>Staff</th><th>Trainings</th><th>CME points</th></tr></thead>
                <tbody>
                  <tr v-for="c in training.cmeByStaff" :key="c.staff_id">
                    <td>{{ c.staff_name }}</td>
                    <td>{{ c.trainings_ytd }}</td>
                    <td><strong :class="cmeClass(c.cme_ytd)">{{ c.cme_ytd }}</strong></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-pad text-center hms-muted">No CME records this year.</div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Recent trainings</div>
            <div class="hms-card-body p-0">
              <table v-if="training.list.length" class="hms-table">
                <thead><tr><th>Title</th><th>Staff</th><th>Type</th><th>Date</th><th>CME</th><th>Outcome</th></tr></thead>
                <tbody>
                  <tr v-for="t in training.list" :key="t.training_id">
                    <td><strong>{{ t.training_title }}</strong><div class="hms-muted-sm">{{ t.provider }}</div></td>
                    <td>{{ t.staff_name }}</td>
                    <td><span class="hms-pill hms-pill-info">{{ t.training_type }}</span></td>
                    <td>{{ t.start_date }}<div v-if="t.end_date && t.end_date !== t.start_date" class="hms-muted-sm">→ {{ t.end_date }}</div></td>
                    <td>{{ t.cme_points }}</td>
                    <td><span class="hms-pill" :class="outcomePill(t.outcome)">{{ t.outcome }}</span></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-pad text-center hms-muted">No trainings recorded.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ APPRAISALS ============ -->
      <div v-if="tab==='appraisals'">
        <div class="hms-stat-row">
          <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ appraisals.stats.total || 0 }}</div><div class="hms-stat-lbl">Total</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ (appraisals.stats.outstanding || 0) + (appraisals.stats.exceeds || 0) }}</div><div class="hms-stat-lbl">Outstanding/Exceeds</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ appraisals.stats.meets || 0 }}</div><div class="hms-stat-lbl">Meets</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ appraisals.stats.below || 0 }}</div><div class="hms-stat-lbl">Below</div></div>
          <div class="hms-stat"><button class="hms-btn hms-btn-success" @click="openAppraisalModal()"><i class="mdi mdi-plus"></i> New Appraisal</button></div>
        </div>
        <table class="hms-table">
          <thead><tr><th>Staff</th><th>Period</th><th>Type</th><th>Appraiser</th><th>Score</th><th>Rating</th><th>Signed</th><th></th></tr></thead>
          <tbody>
            <tr v-for="a in appraisals.list" :key="a.appraisal_id">
              <td>{{ a.staff_name }}<div class="hms-muted-sm">{{ a.staff_dept }}</div></td>
              <td>{{ a.period_label || a.period_year }}<div class="hms-muted-sm">{{ a.period_start }} → {{ a.period_end }}</div></td>
              <td>{{ a.appraisal_type }}</td>
              <td>{{ a.appraiser_name || '—' }}</td>
              <td>{{ a.overall_score != null ? a.overall_score : '—' }}</td>
              <td><span class="hms-pill" :class="ratingPill(a.overall_rating)">{{ a.overall_rating || '—' }}</span></td>
              <td>
                <span :class="a.staff_signed_at ? 'st-good' : 'hms-muted-sm'"><i class="mdi mdi-account"></i> staff</span>
                /
                <span :class="a.appraiser_signed_at ? 'st-good' : 'hms-muted-sm'"><i class="mdi mdi-account-tie"></i> appraiser</span>
              </td>
              <td><button class="hms-btn-link" @click="openAppraisalModal(a)"><i class="mdi mdi-pencil"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ===== LEAVE MODAL ===== -->
    <div v-if="leaveModal" class="hms-modal-bg" @click.self="leaveModal=false">
      <div class="hms-modal" style="width:520px">
        <div class="hms-modal-head"><h5>Apply for leave</h5><button class="hms-icon-btn" @click="leaveModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-12"><label class="hms-label">Staff (ID) *</label><input v-model.number="leaveForm.staff_id" type="number" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Type</label>
              <select v-model="leaveForm.leave_type" class="hms-input">
                <option value="annual">Annual</option><option value="sick">Sick</option>
                <option value="maternity">Maternity</option><option value="paternity">Paternity</option>
                <option value="study">Study</option><option value="compassionate">Compassionate</option>
                <option value="unpaid">Unpaid</option><option value="other">Other</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Days (auto)</label><input :value="leaveDays" disabled class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Start *</label><input v-model="leaveForm.start_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">End *</label><input v-model="leaveForm.end_date" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Reason</label><textarea v-model="leaveForm.reason" class="hms-input" rows="3"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="leaveModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveLeave">{{ saving?'Submitting…':'Submit' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== LICENSE MODAL ===== -->
    <div v-if="licenseModal" class="hms-modal-bg" @click.self="licenseModal=false">
      <div class="hms-modal" style="width:600px">
        <div class="hms-modal-head"><h5>{{ license.license_id ? 'Edit license' : 'Add license' }}</h5><button class="hms-icon-btn" @click="licenseModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Staff (ID) *</label><input v-model.number="license.staff_id" type="number" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Regulator</label>
              <select v-model="license.regulator" class="hms-input">
                <option value="KMPDC">KMPDC (Medical / Dental)</option>
                <option value="NCK">NCK (Nursing)</option>
                <option value="PPB">PPB (Pharmacy)</option>
                <option value="KMLTTB">KMLTTB (Lab)</option>
                <option value="KCHA">KCHA (Clinical Officers)</option>
                <option value="KNDI">KNDI (Nutrition / Dietetics)</option>
                <option value="COHK">COHK (Counsellors)</option>
                <option value="PSK">PSK (Physiotherapy)</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Regulator (other)</label><input v-model="license.regulator_other" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">License # *</label><input v-model="license.license_number" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Category</label><input v-model="license.license_category" class="hms-input" placeholder="e.g. Medical Officer / Registered Nurse" /></div>
            <div class="col-4"><label class="hms-label">Issue date</label><input v-model="license.issue_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Last renewed</label><input v-model="license.last_renewed" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Expires</label><input v-model="license.expiry_date" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Verification URL</label><input v-model="license.verification_url" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Status</label>
              <select v-model="license.renewal_status" class="hms-input">
                <option value="valid">Valid</option><option value="due_soon">Due soon</option>
                <option value="expired">Expired</option><option value="renewing">Renewing</option>
                <option value="suspended">Suspended</option><option value="revoked">Revoked</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="license.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="licenseModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveLicense">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== TRAINING MODAL ===== -->
    <div v-if="trainingModal" class="hms-modal-bg" @click.self="trainingModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>{{ trainingForm.training_id ? 'Edit training' : 'Record training' }}</h5><button class="hms-icon-btn" @click="trainingModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-4"><label class="hms-label">Staff (ID) *</label><input v-model.number="trainingForm.staff_id" type="number" class="hms-input" /></div>
            <div class="col-8"><label class="hms-label">Title *</label><input v-model="trainingForm.training_title" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Type</label>
              <select v-model="trainingForm.training_type" class="hms-input">
                <option value="cme">CME</option><option value="in_service">In-service</option>
                <option value="short_course">Short course</option><option value="workshop">Workshop</option>
                <option value="conference">Conference</option><option value="orientation">Orientation</option>
                <option value="certification">Certification</option><option value="mentorship">Mentorship</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Provider</label><input v-model="trainingForm.provider" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Accrediting body</label><input v-model="trainingForm.accrediting_body" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">CME points</label><input v-model.number="trainingForm.cme_points" type="number" step="0.01" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Hours</label><input v-model.number="trainingForm.duration_hours" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Start</label><input v-model="trainingForm.start_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">End</label><input v-model="trainingForm.end_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Location</label><input v-model="trainingForm.location" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="trainingForm.is_online" :true-value="1" :false-value="0" /> Online</label></div>
            <div class="col-3"><label class="hms-label">Outcome</label>
              <select v-model="trainingForm.outcome" class="hms-input"><option value="completed">Completed</option><option value="in_progress">In progress</option><option value="failed">Failed</option><option value="withdrew">Withdrew</option><option value="planned">Planned</option></select>
            </div>
            <div class="col-6"><label class="hms-label">Certificate #</label><input v-model="trainingForm.certificate_number" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Score / grade</label><input v-model="trainingForm.score_or_grade" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="trainingForm.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="trainingModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveTraining">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== APPRAISAL MODAL ===== -->
    <div v-if="appraisalModal" class="hms-modal-bg" @click.self="appraisalModal=false">
      <div class="hms-modal" style="width:780px">
        <div class="hms-modal-head"><h5>{{ appraisal.appraisal_id ? 'Edit appraisal' : 'New appraisal' }}</h5><button class="hms-icon-btn" @click="appraisalModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-3"><label class="hms-label">Staff (ID) *</label><input v-model.number="appraisal.staff_id" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Year *</label><input v-model.number="appraisal.period_year" type="number" min="2020" max="2099" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Period label</label><input v-model="appraisal.period_label" class="hms-input" placeholder="2026 Q1 / FY26" /></div>
            <div class="col-3"><label class="hms-label">Type</label>
              <select v-model="appraisal.appraisal_type" class="hms-input">
                <option value="annual">Annual</option><option value="mid_year">Mid-year</option>
                <option value="probation">Probation</option><option value="exit">Exit</option>
                <option value="quarterly">Quarterly</option><option value="adhoc">Adhoc</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Appraiser (staff ID) *</label><input v-model.number="appraisal.appraiser_id" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Period start</label><input v-model="appraisal.period_start" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Period end</label><input v-model="appraisal.period_end" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Score</label><input v-model.number="appraisal.overall_score" type="number" step="0.1" class="hms-input" /></div>

            <div class="col-12"><label class="hms-label">Objectives</label><textarea v-model="appraisal.objectives" class="hms-input" rows="3"></textarea></div>
            <div class="col-12"><label class="hms-label">Achievements</label><textarea v-model="appraisal.achievements" class="hms-input" rows="3"></textarea></div>
            <div class="col-12"><label class="hms-label">Competencies</label><textarea v-model="appraisal.competencies" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Gaps identified</label><textarea v-model="appraisal.gaps_identified" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Development plan</label><textarea v-model="appraisal.development_plan" class="hms-input" rows="2"></textarea></div>

            <div class="col-12"><label class="hms-label">Overall rating</label>
              <select v-model="appraisal.overall_rating" class="hms-input">
                <option value="">—</option>
                <option value="outstanding">Outstanding</option>
                <option value="exceeds">Exceeds expectations</option>
                <option value="meets">Meets expectations</option>
                <option value="partially_meets">Partially meets</option>
                <option value="below">Below</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Staff comments</label><textarea v-model="appraisal.staff_comments" class="hms-input" rows="3"></textarea></div>
            <div class="col-6 d-flex align-items-end"><label><input type="checkbox" v-model="appraisal.staff_sign" :true-value="1" :false-value="0" /> Sign as staff (now)</label></div>
            <div class="col-6 d-flex align-items-end"><label><input type="checkbox" v-model="appraisal.appraiser_sign" :true-value="1" :false-value="0" /> Sign as appraiser (now)</label></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="appraisalModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveAppraisal">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "HrSelfService",
  components: { Navigation },
  data() {
    return {
      tab: 'leave', saving: false,
      leave:      { list: [], stats: {}, statusFilter: '' },
      licenses:   { list: [], stats: {} },
      training:   { list: [], cmeByStaff: [] },
      appraisals: { list: [], stats: {} },

      leaveModal: false, leaveForm: this.blankLeave(),
      licenseModal: false, license: this.blankLicense(),
      trainingModal: false, trainingForm: this.blankTraining(),
      appraisalModal: false, appraisal: this.blankAppraisal()
    };
  },
  computed: {
    leaveDays() {
      if (!this.leaveForm.start_date || !this.leaveForm.end_date) return 0;
      return Math.max(1, Math.floor((new Date(this.leaveForm.end_date) - new Date(this.leaveForm.start_date)) / 86400000) + 1);
    }
  },
  mounted() { this.loadAll(); },
  methods: {
    blankLeave() { return { staff_id: null, leave_type: 'annual', start_date: '', end_date: '', reason: '' }; },
    blankLicense() { return { license_id: null, staff_id: null, regulator: 'KMPDC', regulator_other: '', license_number: '', license_category: '', issue_date: '', last_renewed: '', expiry_date: '', verification_url: '', renewal_status: 'valid', notes: '' }; },
    blankTraining() { return { training_id: null, staff_id: null, training_title: '', training_type: 'cme', provider: '', accrediting_body: '', cme_points: 0, start_date: new Date().toISOString().substring(0,10), end_date: '', duration_hours: null, location: '', is_online: 0, certificate_number: '', score_or_grade: '', outcome: 'completed', notes: '' }; },
    blankAppraisal() { return { appraisal_id: null, staff_id: null, period_year: new Date().getFullYear(), period_label: '', appraisal_type: 'annual', appraiser_id: null, period_start: '', period_end: '', objectives: '', achievements: '', competencies: '', gaps_identified: '', development_plan: '', overall_score: null, overall_rating: '', staff_comments: '', staff_sign: 0, appraiser_sign: 0 }; },

    async loadAll() { await this.loadLeaves(); },
    async loadLeaves() {
      try {
        const r = await executeJson({ function: 'getLeaveRequests', status: this.leave.statusFilter }, hms);
        if (r.data && r.data.success) { const d = r.data.data || {}; this.leave.list = d.Leaves || []; this.leave.stats = d.Stats || {}; }
      } catch (e) { console.error(e); }
    },
    async loadLicenses() {
      try {
        const r = await executeJson({ function: 'getStaffLicenses' }, hms);
        if (r.data && r.data.success) { const d = r.data.data || {}; this.licenses.list = d.Licenses || []; this.licenses.stats = d.Stats || {}; }
      } catch (e) { console.error(e); }
    },
    async loadTrainings() {
      try {
        const r = await executeJson({ function: 'getStaffTrainings' }, hms);
        if (r.data && r.data.success) { const d = r.data.data || {}; this.training.list = d.Trainings || []; this.training.cmeByStaff = d.CmeByStaff || []; }
      } catch (e) { console.error(e); }
    },
    async loadAppraisals() {
      try {
        const r = await executeJson({ function: 'getStaffAppraisals' }, hms);
        if (r.data && r.data.success) { const d = r.data.data || {}; this.appraisals.list = d.Appraisals || []; this.appraisals.stats = d.Stats || {}; }
      } catch (e) { console.error(e); }
    },

    // LEAVE
    openLeaveModal() { this.leaveForm = this.blankLeave(); this.leaveModal = true; },
    async saveLeave() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'applyLeave', ...this.leaveForm }, hms);
        if (r.data && r.data.success) { this.leaveModal = false; await this.loadLeaves(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async decideLeave(l, decision) {
      let reason = '';
      if (decision === 'rejected') {
        reason = prompt('Rejection reason?');
        if (reason === null) return;
      }
      const r = await executeJson({ function: 'decideLeave', leave_id: l.leave_id, decision, rejection_reason: reason }, hms);
      if (r.data && r.data.success) await this.loadLeaves();
      else alert((r.data && r.data.message) || 'Failed.');
    },

    // LICENSES
    openLicenseModal(l) { this.license = l ? { ...this.blankLicense(), ...l } : this.blankLicense(); this.licenseModal = true; },
    async saveLicense() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveStaffLicense', ...this.license }, hms);
        if (r.data && r.data.success) { this.licenseModal = false; await this.loadLicenses(); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // TRAINING
    openTrainingModal(t) { this.trainingForm = t ? { ...this.blankTraining(), ...t } : this.blankTraining(); this.trainingModal = true; },
    async saveTraining() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveStaffTraining', ...this.trainingForm }, hms);
        if (r.data && r.data.success) { this.trainingModal = false; await this.loadTrainings(); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // APPRAISAL
    openAppraisalModal(a) { this.appraisal = a ? { ...this.blankAppraisal(), ...a } : this.blankAppraisal(); this.appraisalModal = true; },
    async saveAppraisal() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveStaffAppraisal', ...this.appraisal }, hms);
        if (r.data && r.data.success) { this.appraisalModal = false; await this.loadAppraisals(); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // formatting
    leaveRow(l) { if (l.status === 'pending') return 'row-warn'; return ''; },
    leaveTypePill(t) { return ({ annual: 'hms-pill-info', sick: 'hms-pill-warn', maternity: 'hms-pill-info', paternity: 'hms-pill-info', study: 'hms-pill-info', compassionate: 'hms-pill-warn', unpaid: 'hms-pill-muted', other: 'hms-pill-muted' })[t] || ''; },
    leaveStatusPill(s) { return ({ pending: 'hms-pill-warn', approved: 'hms-pill-success', rejected: 'hms-pill-danger', cancelled: 'hms-pill-muted' })[s] || ''; },
    licenseRow(l) {
      if (l.renewal_status === 'expired' || l.renewal_status === 'revoked' || l.renewal_status === 'suspended') return 'row-action';
      if (l.renewal_status === 'due_soon') return 'row-warn';
      return '';
    },
    licenseStatusPill(s) { return ({ valid: 'hms-pill-success', due_soon: 'hms-pill-warn', expired: 'hms-pill-danger', renewing: 'hms-pill-info', suspended: 'hms-pill-danger', revoked: 'hms-pill-danger' })[s] || ''; },
    daysToExpiryClass(d) { if (d == null) return ''; if (d < 0) return 'st-bad'; if (d < 60) return 'st-warn'; return 'st-good'; },
    cmeClass(p) { const v = parseFloat(p || 0); if (v >= 30) return 'st-good'; if (v >= 15) return 'st-warn'; return 'st-bad'; },
    outcomePill(o) { return ({ completed: 'hms-pill-success', in_progress: 'hms-pill-info', failed: 'hms-pill-danger', withdrew: 'hms-pill-warn', planned: 'hms-pill-muted' })[o] || ''; },
    ratingPill(r) { return ({ outstanding: 'hms-pill-success', exceeds: 'hms-pill-success', meets: 'hms-pill-info', partially_meets: 'hms-pill-warn', below: 'hms-pill-danger' })[r] || 'hms-pill-muted'; }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 26px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-success { color: #276749; } .hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }
.hms-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }

.st-bad { color: #c53030; font-weight: 600; }
.st-warn { color: #b45309; font-weight: 600; }
.st-good { color: #276749; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
